import Alpine from "alpinejs";
import Fuse from "fuse.js";
import NiceSelect from "nice-select2/dist/js/nice-select2.js";
import L from "leaflet";

window.L = L;
import "leaflet.markercluster";

const SWITZERLAND_BOUNDS = [
    [45.818, 5.956],
    [47.808, 10.492],
];
const MIN_ZOOM = 6;
const MAX_ZOOM = 15;
const SWITZERLAND_CENTER = [46.8182, 8.2275];
const PLACE_LOOKUP_DELAY = 400;
const FIT_DURATION = 0.7;
const FIT_MAX_ZOOM = 12;
const PIN_EDGE_GUTTER = 56;
const PIN_OVERLAY_GUTTER = 20;
const PIN_VIEW = { width: 40, height: 53, tipX: 20, tipY: 52 };
const OVERLAY_ANIM_MS = 360;
const OVERLAY_ANIM_EASE = "cubic-bezier(0.165, 0.84, 0.44, 1)";
const ARROW_UP_RIGHT_ICON = `<svg data-icon-name="arrow-up-right" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 9 10" fill="none" aria-hidden="true"><path fill="currentColor" d="M1.31252 8.94105L7.39196 2.69475L7.39196 8.1517C7.39279 8.3621 7.47451 8.56364 7.61931 8.71242C7.76412 8.8612 7.96027 8.94516 8.16505 8.94601C8.26584 8.9461 8.36565 8.92576 8.45878 8.88617C8.55191 8.84658 8.63653 8.78851 8.70779 8.71529C8.77906 8.64207 8.83557 8.55513 8.87411 8.45944C8.91264 8.36376 8.93243 8.26121 8.93235 8.15766L8.93235 0.788388C8.93243 0.684836 8.91264 0.582285 8.87411 0.4866C8.83557 0.390915 8.77906 0.303974 8.70779 0.230752C8.63653 0.157529 8.55191 0.0994632 8.45878 0.0598734C8.36565 0.0202836 8.26584 -5.23583e-05 8.16505 2.94274e-05L0.992654 2.8744e-05C0.789153 2.8744e-05 0.593989 0.0830874 0.450093 0.230933C0.306196 0.378779 0.225356 0.579301 0.225356 0.788387C0.225356 0.997473 0.306196 1.198 0.450092 1.34584C0.593989 1.49369 0.789153 1.57675 0.992654 1.57675L6.30382 1.57675L0.225356 7.82206C0.0810606 7.97031 -3.96692e-06 8.17139 -3.96692e-06 8.38106C-4.2995e-06 8.59072 0.081061 8.7918 0.225356 8.94006C0.369652 9.08831 0.565358 9.1716 0.769423 9.1716C0.973487 9.1716 1.16919 9.08831 1.31349 8.94006L1.31252 8.94105Z"/></svg>`;
const PLUS_ICON = `<svg class="c-location-card__action-icon" width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M5 2.15v5.7M2.15 5h5.7" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>`;
const CLOSE_ICON = `<svg class="c-location-card__action-icon" width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M2.55 2.55l4.9 4.9M7.45 2.55l-4.9 4.9" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>`;

function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function asHtml(value) {
    return value == null ? "" : String(value);
}

function isPresent(value) {
    return !(value == null || String(value).trim() === "");
}

function stripTags(value) {
    return String(value ?? "").replace(/<[^>]*>/g, "").trim();
}

function foldPlaceQuery(value) {
    return String(value ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
}

function isCompletePlaceQuery(query) {
    const trimmed = String(query ?? "").trim();
    if (!trimmed) {
        return false;
    }
    const compact = trimmed.replace(/\s/g, "");
    if (/^\d+$/.test(compact)) {
        return compact.length === 4;
    }
    return foldPlaceQuery(trimmed).length >= 3;
}

function distanceKm(fromLat, fromLon, toLat, toLon) {
    const toRad = (degrees) => (degrees * Math.PI) / 180;
    const dLat = toRad(toLat - fromLat);
    const dLon = toRad(toLon - fromLon);
    const lat1 = toRad(fromLat);
    const lat2 = toRad(toLat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

const PLACE_FUSE_OPTIONS = {
    threshold: 0.35,
    ignoreLocation: true,
    minMatchCharLength: 2,
    keys: [
        { name: "city", weight: 0.75 },
        { name: "zip", weight: 0.25 },
    ],
    getFn(object, path) {
        const raw = Fuse.config.getFn(object, path);
        const value = Array.isArray(raw) ? raw.join(" ") : raw;
        return foldPlaceQuery(value);
    },
};

/** Keep in sync with LocationCopy::telHref — digits and + only. */
function telHref(phone, explicitHref) {
    if (isPresent(explicitHref)) {
        return String(explicitHref).trim();
    }
    const sanitized = String(phone ?? "").replace(/[^\d+]/g, "");
    return sanitized ? `tel:${sanitized}` : "";
}

function isFlag(value) {
    return value === true || value === 1 || value === "1" || value === "true";
}

function factList(location) {
    const facts = location?.facts;
    const items = Array.isArray(facts) ? facts : Object.values(facts || {});
    return items.filter((fact) => fact && (isPresent(stripTags(fact.label)) || isPresent(fact.value)));
}

function locationRow(label, valueHtml, { headClass = "", valueClass = "", valueTag = "div", rowClass = "", alwaysHead = false } = {}) {
    if (!isPresent(valueHtml) && valueHtml !== "") {
        return "";
    }
    const head = label || alwaysHead
        ? `<div class="c-location-card__row-head${headClass ? ` ${headClass}` : ""}"><span class="c-location-card__bullet" aria-hidden="true"></span>${label ? `<p class="c-location-card__label">${escapeHtml(label)}</p>` : ""}</div>`
        : "";
    return `<div class="c-location-card__row${rowClass ? ` ${rowClass}` : ""}">${head}<${valueTag} class="c-location-card__value${valueClass ? ` ${valueClass}` : ""}">${valueHtml}</${valueTag}></div>`;
}

function teaserHoursHtml(location, labels, { wrapDetails = false } = {}) {
    const teaser = asHtml(location.teaser).trim();
    const hours = asHtml(location.openingHours).trim();
    const bookingHtml = asHtml(location.bookingHtml).trim();
    const bookingPrefix = stripTags(location.bookingPrefix);
    const teaserBlock = teaser ? `<div class="c-location-card__teaser"><div class="c-bodytext">${teaser}</div></div>` : "";
    const bookingBlock = bookingHtml
        ? bookingPrefix
            ? locationRow(bookingPrefix, `<div class="c-bodytext">${bookingHtml}</div>`)
            : `<div class="c-location-card__booking"><div class="c-bodytext">${bookingHtml}</div></div>`
        : "";
    const hoursBlock = hours
        ? locationRow(labels.hours, `<div class="c-bodytext">${hours}</div>`, { valueClass: "c-location-card__hours" })
        : "";
    const inner = `${teaserBlock}${bookingBlock}${hoursBlock}`;
    if (!inner) {
        return "";
    }
    if (!wrapDetails) {
        return inner;
    }
    return `<div class="c-location-card__details"><div class="c-location-card__details-inner">${inner}</div></div>`;
}

function locationCardHtml(location, labels, variant) {
    const isMobile = variant === "overlayMobile";
    const title = escapeHtml(location.title);
    const city = escapeHtml(location.city);
    const street = escapeHtml(location.street);
    const zip = escapeHtml(location.zip);
    const phone = escapeHtml(location.phone);
    const email = escapeHtml(location.email);
    const website = escapeHtml(location.websiteHref);
    const addressHtml = `${street}${street ? "<br />" : ""}${zip}${zip && city ? " " : ""}${city}`;
    const heading = `<div class="c-location-card__heading">${isMobile && city ? `<p class="c-location-card__city">${city}</p>` : ""}<h3 class="c-headline display-h4 c-location-card__title">${title}</h3></div>`;
    const factsFixed = factList(location)
        .map((fact) =>
            locationRow(stripTags(fact.label), `<div class="c-bodytext">${asHtml(fact.value)}</div>`, {
                rowClass: "c-location-fact",
                alwaysHead: true,
            }),
        )
        .join("");

    const addressRow =
        isPresent(location.street) || isPresent(location.city)
            ? locationRow(labels.address || "", addressHtml, {
                  headClass: isMobile ? "c-location-card__expanded-only" : "",
                  valueClass: "c-location-card__address",
              })
            : "";
    const phoneRow =
        !isMobile && isPresent(location.phone)
            ? locationRow(
                  labels.phone || "",
                  `<a href="${escapeHtml(telHref(location.phone, location.phoneHref))}">${phone}</a>`,
                  { valueTag: "p", valueClass: "c-bodytext" },
              )
            : "";
    const emailRow =
        !isMobile && isPresent(location.email)
            ? locationRow(labels.email || "", `<a href="mailto:${email}">${email}</a>`, {
                  valueTag: "p",
                  valueClass: "c-bodytext",
              })
            : "";
    const websiteRow =
        !isMobile && isPresent(location.websiteHref)
            ? locationRow(
                  labels.website || "",
                  `<a href="${website}" target="_blank" rel="noopener noreferrer">${website}</a>`,
                  { valueTag: "p", valueClass: "c-bodytext" },
              )
            : "";

    const showMore = isFlag(location.hasDetailPage) && isPresent(location.uri);
    const showExpand = isMobile && !isFlag(location.hasDetailPage);
    const moreClass = `c-button c-button--secondary c-location-card__action${isMobile ? " c-location-card__compact-only" : ""}`;
    const moreButton = showMore
        ? `<a class="${moreClass}" href="${escapeHtml(location.uri)}">${escapeHtml(labels.more)}${ARROW_UP_RIGHT_ICON}</a>`
        : "";
    const expandButtons = showExpand
        ? `<button type="button" class="c-button c-button--secondary c-location-card__action c-location-card__compact-only" x-on:click.stop="$dispatch('map-overlay-expand')">${escapeHtml(labels.more)}${PLUS_ICON}</button><button type="button" class="c-button c-button--secondary c-location-card__action c-location-card__expanded-only" x-on:click.stop="$dispatch('map-overlay-collapse')">${escapeHtml(labels.close)}${CLOSE_ICON}</button>`
        : "";
    const destination = [location.street, location.zip, location.city].filter(isPresent).join(",");
    const directions = `<a target="_blank" rel="noopener noreferrer" x-data="directionsLink" href="https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}" data-map-address="${escapeHtml(destination)}" class="c-button c-directions-link c-button--ghost c-location-card__action">${escapeHtml(labels.directions)}${ARROW_UP_RIGHT_ICON}</a>`;

    return `<article class="c-location-card c-location-card--${variant}" x-data="LocationCardFade()" :class="showFade && 'has-scroll-fade'"><div class="c-location-card__body" x-ref="body" x-on:scroll.passive="syncFade()">${heading}${teaserHoursHtml(location, labels, { wrapDetails: isMobile })}${addressRow}${phoneRow}${emailRow}${websiteRow}${factsFixed}</div><div class="c-location-card__actions">${moreButton}${expandButtons}${directions}</div></article>`;
}

class ZoomControl extends L.Control {
    constructor(plusSvg, minusSvg) {
        super({ position: "bottomright" });
        this.plusSvg = plusSvg;
        this.minusSvg = minusSvg;
    }

    onAdd(map) {
        const container = L.DomUtil.create("div", "c-map-section__zoom");
        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        [
            [this.plusSvg, () => map.zoomIn()],
            [this.minusSvg, () => map.zoomOut()],
        ].forEach(([svg, handler]) => {
            const btn = L.DomUtil.create("button", "c-map-section__zoom-btn", container);
            btn.type = "button";
            btn.innerHTML = svg;
            L.DomEvent.on(btn, "click", handler);
        });

        return container;
    }
}

function normalizeLocations(raw) {
    return (Array.isArray(raw) ? raw : Object.values(raw || {}))
        .map((location) => ({
            ...location,
            canton: String(location?.canton ?? "").trim(),
            city: stripTags(location?.city),
            zip: stripTags(location?.zip),
            lat: location?.lat == null ? null : Number(location.lat),
            lon: location?.lon == null ? null : Number(location.lon),
            facts: factList(location),
        }))
        .filter((location) => location && Number.isFinite(location.lat) && Number.isFinite(location.lon));
}

document.addEventListener("alpine:init", () => {
    Alpine.data("MapSection", (config = {}) => ({
        mode: config.mode || "finder",
        locationsUrl: config.locationsUrl || "",
        locationsLoading: Boolean(config.locationsUrl),
        locations: config.locationsUrl ? [] : normalizeLocations(config.locations),
        labels: config.labels || {},
        activeId: config.previewActiveId || null,
        overlayExpanded: false,
        mobileView: config.previewMobileView || "map",
        canton: "",
        zip: "",
        leafletMap: null,
        clusterGroup: null,
        markersById: {},
        ignoreMapClick: false,
        programmaticView: false,
        placeFuse: null,
        zipFitTimer: null,
        placeTimer: null,
        placeRequest: 0,
        placePoint: null,
        nearestLocations: [],
        nearestPending: false,
        focusActiveTimer: null,
        pinNudgeTimer: null,
        programmaticViewTimer: null,
        clusterFitToken: 0,
        compactOverlayBox: null,

        get filteredLocations() {
            const query = (this.zip || "").trim();
            const foldedQuery = foldPlaceQuery(query);
            const zipDigits = query.replace(/\D/g, "");
            const isZipOnly = query !== "" && /^\d+$/.test(query.replace(/\s/g, ""));
            const useFuzzyPlace =
                Boolean(foldedQuery)
                && !isZipOnly
                && this.placeFuse
                && foldedQuery.length >= PLACE_FUSE_OPTIONS.minMatchCharLength;
            const placeIds = useFuzzyPlace
                ? new Set(this.placeFuse.search(foldedQuery).map((result) => result.item.id))
                : null;

            return this.locations.filter((location) => {
                if (this.canton && location.canton !== this.canton) {
                    return false;
                }
                if (!query) {
                    return true;
                }
                if (isZipOnly) {
                    return (location.zip || "").startsWith(zipDigits);
                }
                if (placeIds) {
                    return placeIds.has(location.id);
                }
                return foldPlaceQuery(location.city).startsWith(foldedQuery);
            });
        },

        get visibleLocations() {
            if (this.filteredLocations.length) {
                return this.filteredLocations;
            }
            return this.nearestLocations;
        },

        get showingNearest() {
            return this.filteredLocations.length === 0 && this.nearestLocations.length > 0;
        },

        nearestHeading() {
            const template = this.labels.nearestIntro || "";
            return template.replace("{query}", (this.zip || "").trim());
        },

        nearestPool() {
            if (!this.canton) {
                return this.locations;
            }
            return this.locations.filter((location) => location.canton === this.canton);
        },

        rankNearest(point) {
            return this.nearestPool()
                .map((location) => ({
                    location,
                    distance: distanceKm(point.lat, point.lon, location.lat, location.lon),
                }))
                .sort((left, right) => left.distance - right.distance)
                .slice(0, 3)
                .map((entry) => entry.location);
        },

        invalidatePlaceLookup() {
            clearTimeout(this.placeTimer);
            this.placeRequest += 1;
            this.nearestPending = false;
            this.nearestLocations = [];
            this.placePoint = null;
        },

        syncNearest() {
            const query = (this.zip || "").trim();
            const eligible = isCompletePlaceQuery(query)
                && this.filteredLocations.length === 0
                && this.nearestPool().length > 0;
            if (!eligible) {
                this.invalidatePlaceLookup();
                return;
            }
            if (this.placePoint && this.placePoint.query === query) {
                clearTimeout(this.placeTimer);
                this.placeRequest += 1;
                this.nearestPending = false;
                this.nearestLocations = this.rankNearest(this.placePoint);
                return;
            }
            this.nearestLocations = [];
            this.nearestPending = true;
            clearTimeout(this.placeTimer);
            this.placeRequest += 1;
            const request = this.placeRequest;
            this.placeTimer = setTimeout(() => {
                this.fetchPlace(query, request);
            }, PLACE_LOOKUP_DELAY);
        },

        async fetchPlace(query, request) {
            if (request !== this.placeRequest) {
                return;
            }
            try {
                const response = await fetch(`/api/map-place?q=${encodeURIComponent(query)}`);
                if (request !== this.placeRequest || (this.zip || "").trim() !== query) {
                    return;
                }
                if (!response.ok) {
                    throw new Error("map place");
                }
                const body = await response.json();
                const lat = Number(body?.lat);
                const lon = Number(body?.lon);
                if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
                    throw new Error("map place");
                }
                this.placePoint = { query, lat, lon };
                this.nearestLocations = this.rankNearest(this.placePoint);
            } catch {
                if (request !== this.placeRequest) {
                    return;
                }
                this.placePoint = null;
                this.nearestLocations = [];
            }
            if (request !== this.placeRequest) {
                return;
            }
            this.nearestPending = false;
            this.syncMarkers({ animate: true });
        },

        async loadLocations() {
            try {
                const response = await fetch(this.locationsUrl);
                if (!response.ok) {
                    throw new Error("map locations");
                }
                const body = await response.json();
                this.locations = normalizeLocations(body?.locations);
            } catch {
                this.locations = [];
            }
            this.bootLocations();
        },

        bootLocations() {
            this.locationsLoading = false;
            this.placeFuse = new Fuse(this.locations, PLACE_FUSE_OPTIONS);
            this.syncCantonOptions();
            this.syncNearest();
            this.$nextTick(() => {
                if (this.activeId) {
                    this.syncOverlayPanes(this.activeId, null);
                }
                this.initMap();
                this.syncMarkers({ fit: false });
                this.scheduleFitVisible();
                this.syncCantonPlaceholder();
                setTimeout(() => this.syncCantonPlaceholder(), 0);
            });
        },

        init() {
            this.$nextTick(() => this.initMap());
            if (this.locationsUrl) {
                this.loadLocations();
            } else {
                this.bootLocations();
            }

            this.$watch("canton", () => {
                this.syncCantonPlaceholder();
                this.syncNearest();
                this.syncMarkers({ animate: true });
            });
            this.$watch("zip", () => {
                this.syncNearest();
                this.syncMarkers({ animate: false, fit: false });
                this.scheduleZipFit();
            });
            this.$watch("mobileView", (view) => {
                if (view === "list") {
                    this.activeId = null;
                    this.overlayExpanded = false;
                }
                this.$nextTick(() => {
                    this.leafletMap?.invalidateSize({ animate: true });
                    this.updateMaxBounds();
                    if (view !== "map") {
                        return;
                    }
                    if (this.activeId) {
                        this.scheduleFocusActive();
                    } else {
                        this.scheduleFitVisible({ animate: true });
                    }
                });
            });
            this.$watch("activeId", (id, prev) => {
                this.syncOverlayPanes(id, prev);
                Object.entries(this.markersById).forEach(([markerId, marker]) => {
                    const element = marker.getElement();
                    if (element) {
                        element.classList.toggle("is-active", markerId === id);
                    }
                });
                this.$nextTick(() => {
                    this.updateMaxBounds();
                    this.scrollListTo(id);
                    if (id) {
                        this.scheduleFocusActive();
                    }
                });
            });

            this._finderMobile = this.isMobileFinder();
            window.addEventListener(
                "resize",
                () => {
                    const finderMobile = this.isMobileFinder();
                    if (finderMobile !== this._finderMobile) {
                        this._finderMobile = finderMobile;
                        this.syncMarkers({ animate: false, fit: false });
                    }
                    this.syncSingleMapHeight();
                    this.leafletMap?.invalidateSize();
                    this.updateMaxBounds();
                },
                { passive: true },
            );
            window.addEventListener("beforeprint", () => {
                if (this.$refs.mapElement) {
                    this.$refs.mapElement.style.height = "";
                }
                this.leafletMap?.invalidateSize({ animate: false });
            });
            window.addEventListener("afterprint", () => {
                this.syncSingleMapHeight();
                this.leafletMap?.invalidateSize({ animate: false });
            });
        },

        syncCantonOptions() {
            const select = this.$el.querySelector("select.c-map-section__select");
            if (!select) {
                return;
            }

            const codes = [
                ...new Set(this.locations.map((location) => location.canton).filter(Boolean)),
            ].sort((a, b) => a.localeCompare(b, "de"));

            while (select.options.length > 1) {
                select.remove(1);
            }
            codes.forEach((code) => {
                const option = document.createElement("option");
                option.value = code;
                option.textContent = code;
                select.appendChild(option);
            });

            const existing = select.nextElementSibling;
            if (existing?.classList.contains("nice-select")) {
                existing.remove();
            }
            new NiceSelect.default(select, {
                searchable: false,
                placeholder: select.dataset.placeholder ?? null,
            });
        },

        syncCantonPlaceholder() {
            const current = this.$el.querySelector(".c-map-section__filter .nice-select .current");
            if (!current || this.canton) {
                return;
            }
            current.textContent = this.labels.canton || "";
        },

        onZipInput(event) {
            this.zip = event.target.value || "";
        },

        scheduleZipFit() {
            clearTimeout(this.zipFitTimer);
            this.zipFitTimer = setTimeout(() => {
                if (this.nearestPending) {
                    return;
                }
                const locations = this.visibleLocations;
                if (!locations.length) {
                    return;
                }
                this.fitLocations(locations, { animate: true });
            }, PLACE_LOOKUP_DELAY);
        },

        initMap() {
            const mapEl = this.$refs.mapElement;
            if (!mapEl || this.leafletMap) {
                return;
            }

            const map = L.map(mapEl, {
                attributionControl: true,
                zoomControl: false,
                maxBoundsViscosity: 0.55,
                minZoom: MIN_ZOOM,
                maxZoom: MAX_ZOOM,
            });
            map.attributionControl.setPosition("bottomleft");
            map.attributionControl.setPrefix(false);

            map.setView(SWITZERLAND_CENTER, MIN_ZOOM);

            const tileOptions = {
                minZoom: MIN_ZOOM,
                maxZoom: MAX_ZOOM,
                keepBuffer: 6,
                updateWhenIdle: false,
                updateWhenZooming: true,
            };

            L.tileLayer("/tile-proxy/{z}/{x}/{y}{r}.png?v=png", {
                ...tileOptions,
                attribution: this.labels.attribution || "",
            }).addTo(map);
            L.tileLayer("/tile-proxy/{z}/{x}/{y}{r}.png?layer=ref&v=png", tileOptions).addTo(map);

            new ZoomControl(this.$refs.plusIcon.innerHTML, this.$refs.minusIcon.innerHTML).addTo(map);

            map.on("zoomend", () => this.updateMaxBounds({ pan: false }));
            map.on("zoomstart", () => {
                if (this.programmaticView || this.ignoreMapClick || this.mode !== "finder") {
                    return;
                }
                this.closeDetails();
            });
            map.on("click", () => {
                if (this.ignoreMapClick || this.mode !== "finder") {
                    return;
                }
                this.closeDetails({ restoreView: true });
            });

            this.leafletMap = map;
            this.updateMaxBounds({ pan: false });
            this.observeSingleMapHeight();
            this.$nextTick(() => {
                this.syncSingleMapHeight();
                this.scheduleFitVisible();
            });
        },

        observeSingleMapHeight() {
            const cardEl = this.singleCardEl();
            if (!cardEl) {
                return;
            }
            this.pageResizeObserver?.disconnect();
            this.pageResizeObserver = new ResizeObserver(() => {
                this.syncSingleMapHeight();
            });
            this.pageResizeObserver.observe(cardEl);
        },

        singleCardEl() {
            if (this.mode !== "single") {
                return null;
            }
            return this.$el.querySelector(".c-map-section__page-card") || this.$el.querySelector(".c-location-card--page");
        },

        syncSingleMapHeight() {
            const mapEl = this.$refs.mapElement;
            const cardEl = this.singleCardEl();
            if (!mapEl || this.mode !== "single") {
                return;
            }
            if (!cardEl || !window.matchMedia("(min-width: 1024px)").matches) {
                if (mapEl.style.height) {
                    mapEl.style.height = "";
                    this.leafletMap?.invalidateSize({ animate: false });
                }
                return;
            }
            const next = Math.round(cardEl.getBoundingClientRect().height);
            if (next <= 0 || mapEl.style.height === `${next}px`) {
                return;
            }
            mapEl.style.height = `${next}px`;
            this.leafletMap?.invalidateSize({ animate: false });
        },

        isDesktopFinder() {
            return this.mode === "finder" && window.matchMedia("(min-width: 1024px)").matches;
        },

        cssLengthPx(name) {
            const host = this.$el.closest(".c-map-section") || this.$el;
            const value = getComputedStyle(host).getPropertyValue(name).trim();
            if (!value) {
                return 0;
            }
            if (value.endsWith("rem")) {
                const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
                return parseFloat(value) * rem;
            }
            return parseFloat(value) || 0;
        },

        leftCoverPx() {
            if (!this.isDesktopFinder()) {
                return 0;
            }

            const mapEl = this.$refs.mapElement;
            const mapLeft = mapEl ? mapEl.getBoundingClientRect().left : 0;
            const side = this.cssLengthPx("--map-side-width");
            const gap = this.cssLengthPx("--map-side-gap");
            let cover = 0;

            const chrome = this.$el.querySelector(".c-map-section__chrome");
            if (chrome && chrome.offsetParent !== null) {
                cover = Math.max(cover, chrome.getBoundingClientRect().right - mapLeft);
            }

            const list = this.$refs.locationList;
            if (list && list.offsetParent !== null) {
                cover = Math.max(cover, list.getBoundingClientRect().right - mapLeft);
            }

            this.overlayPanes().forEach((pane) => {
                if (!pane || pane.offsetParent === null) {
                    return;
                }
                const opacity = parseFloat(getComputedStyle(pane).opacity);
                if (!(opacity > 0.01)) {
                    return;
                }
                cover = Math.max(cover, pane.getBoundingClientRect().right - mapLeft);
            });

            const panel = this.$el.querySelector(".c-map-section__panel");
            const panelPadRight =
                panel && panel.offsetParent !== null
                    ? parseFloat(getComputedStyle(panel).paddingRight) || 0
                    : 0;
            cover += panelPadRight;

            if (cover <= 0) {
                cover = side + 32 + (this.activeId ? side + gap : 0);
            }

            return Math.max(0, Math.round(cover + PIN_EDGE_GUTTER));
        },

        overlayCoverPx() {
            if (!this.isMobileFinder() || !this.activeId) {
                return 0;
            }

            const overlay = this.activeOverlayEl();
            const mapEl = this.$refs.mapElement;
            if (!overlay || !mapEl || overlay.offsetParent === null) {
                return 0;
            }

            const cover = overlay.getBoundingClientRect().bottom - mapEl.getBoundingClientRect().top;
            if (cover <= 0) {
                return 0;
            }

            return Math.round(cover + PIN_OVERLAY_GUTTER);
        },

        mapPadding() {
            const left = this.leftCoverPx();
            if (this.isMobileFinder()) {
                const mapEl = this.$refs.mapElement;
                const mapH = mapEl?.clientHeight || 0;
                const cover = this.overlayCoverPx();
                const pinH = this.finderPinSize().pin[1];
                const slot = pinH + 28;
                let top = Math.max(32, cover);
                let bottom = 56;
                if (mapH && cover) {
                    bottom = Math.max(16, mapH - top - slot);
                    if (top + slot + 16 > mapH) {
                        top = Math.max(32, mapH - slot - 16);
                        bottom = 16;
                    }
                }
                return {
                    paddingTopLeft: [24, top],
                    paddingBottomRight: [24, bottom],
                };
            }
            return {
                paddingTopLeft: [left, 32],
                paddingBottomRight: [72, 96],
            };
        },

        whenMapLaidOut(callback) {
            const map = this.leafletMap;
            const mapEl = this.$refs.mapElement;
            if (!map || !mapEl) {
                return;
            }

            const run = () => {
                map.invalidateSize({ animate: false });
                requestAnimationFrame(() => {
                    requestAnimationFrame(callback);
                });
            };

            if (mapEl.clientWidth > 0 && mapEl.clientHeight > 0) {
                run();
                return;
            }

            const observer = new ResizeObserver(() => {
                if (mapEl.clientWidth > 0 && mapEl.clientHeight > 0) {
                    observer.disconnect();
                    run();
                }
            });
            observer.observe(mapEl);
        },

        scheduleFitVisible({ animate = false } = {}) {
            clearTimeout(this.fitVisibleTimer);
            this.fitVisibleTimer = setTimeout(() => {
                this.whenMapLaidOut(() => {
                    this.updateMaxBounds({ pan: false });
                    this.fitLocations(this.visibleLocations, { animate });
                });
            }, 0);
        },

        scheduleFocusActive() {
            clearTimeout(this.focusActiveTimer);
            this.focusActiveTimer = setTimeout(() => this.focusActiveLocation(), 0);
        },

        focusActiveLocation() {
            const map = this.leafletMap;
            const id = this.activeId;
            if (!map || !id) {
                return;
            }

            const location = this.locations.find((item) => item.id === id);
            if (!location) {
                return;
            }

            this.whenMapLaidOut(() => {
                if (this.activeId !== id) {
                    return;
                }
                this.updateMaxBounds({ pan: false });
                this.fitLocations([location], {
                    animate: true,
                    maxZoom: Math.max(map.getZoom(), FIT_MAX_ZOOM),
                });
                this.queuePinNudge(location);
            });
        },

        queuePinNudge(location) {
            clearTimeout(this.pinNudgeTimer);
            const delay = (FIT_DURATION * 1000) + (this.isDesktopFinder() ? 320 : 50);
            this.pinNudgeTimer = setTimeout(() => this.nudgePinIntoView(location), delay);
        },

        nudgePinIntoView(location) {
            const map = this.leafletMap;
            if (!map || !location || this.activeId !== location.id) {
                return;
            }
            const marker = this.markersById[location.id];
            if (marker?._icon) {
                this.nudgeIconsIntoView([marker]);
                return;
            }
            if (typeof map.panInside !== "function") {
                return;
            }
            this.beginProgrammaticView();
            map.panInside(L.latLng(location.lat, location.lon), {
                ...this.mapPadding(),
                animate: true,
            });
        },

        nudgeIconsIntoView(markers) {
            const map = this.leafletMap;
            if (!map || typeof map.panBy !== "function") {
                return;
            }
            const icons = (markers || []).map((marker) => marker?._icon).filter(Boolean);
            if (!icons.length) {
                return;
            }

            const padding = this.mapPadding();
            const size = map.getSize();
            const minX = padding.paddingTopLeft[0];
            const minY = padding.paddingTopLeft[1];
            const maxX = size.x - padding.paddingBottomRight[0];
            const maxY = size.y - padding.paddingBottomRight[1];
            const mapBox = map.getContainer().getBoundingClientRect();
            let panX = 0;
            let panY = 0;

            icons.forEach((icon) => {
                const box = icon.getBoundingClientRect();
                const left = box.left - mapBox.left;
                const right = box.right - mapBox.left;
                const top = box.top - mapBox.top;
                const bottom = box.bottom - mapBox.top;
                if (left < minX) {
                    panX = Math.min(panX, left - minX);
                }
                if (right > maxX) {
                    panX = Math.max(panX, right - maxX);
                }
                if (top < minY) {
                    panY = Math.min(panY, top - minY);
                }
                if (bottom > maxY) {
                    panY = Math.max(panY, bottom - maxY);
                }
            });

            if (panX === 0 && panY === 0) {
                return;
            }
            this.beginProgrammaticView();
            map.panBy([panX, panY], { animate: true });
        },

        updateMaxBounds({ pan = true } = {}) {
            const map = this.leafletMap;
            if (!map) {
                return;
            }

            const padLeft = this.leftCoverPx();
            const size = map.getSize();
            const extraRight = 80;
            const extraY = 80;
            const base = L.latLngBounds(SWITZERLAND_BOUNDS);
            const sw = map.project(base.getSouthWest());
            const ne = map.project(base.getNorthEast());
            const expanded = L.latLngBounds(
                map.unproject(L.point(sw.x - padLeft - extraRight, sw.y + extraY + size.y * 0.05)),
                map.unproject(L.point(ne.x + extraRight, ne.y - extraY)),
            );

            if (pan) {
                map.setMaxBounds(expanded);
                return;
            }

            map.options.maxBounds = expanded;
        },

        syncMarkers({ animate = false, fit = true } = {}) {
            if (!this.leafletMap) {
                return;
            }

            if (this.clusterGroup) {
                this.leafletMap.removeLayer(this.clusterGroup);
            }

            this.markersById = {};
            const markerTemplate = this.$refs.markerTemplate.innerHTML;
            const useCluster = this.mode === "finder";
            const pin = this.finderPinSize();
            const group = useCluster
                ? L.markerClusterGroup({
                      showCoverageOnHover: false,
                      zoomToBoundsOnClick: false,
                      spiderfyOnMaxZoom: false,
                      maxClusterRadius: 48,
                      iconCreateFunction(cluster) {
                          const count = cluster.getChildCount();
                          return L.divIcon({
                              html: `<span>${count}</span>`,
                              className: "c-map-section__cluster",
                              iconSize: pin.cluster,
                              iconAnchor: pin.clusterAnchor,
                          });
                      },
                  })
                : L.layerGroup();

            this.visibleLocations.forEach((location) => {
                const marker = L.marker([location.lat, location.lon], {
                    icon: L.divIcon({
                        className:
                            "c-map-section__marker" + (this.activeId === location.id ? " is-active" : ""),
                        html: markerTemplate,
                        iconSize: pin.pin,
                        iconAnchor: pin.pinAnchor,
                    }),
                    title: location.title,
                });

                if (useCluster) {
                    marker.on("click", () => {
                        this.ignoreMapClick = true;
                        this.showDetails(location.id);
                        this.$nextTick(() => {
                            this.ignoreMapClick = false;
                        });
                    });
                }

                group.addLayer(marker);
                this.markersById[location.id] = marker;
            });

            if (useCluster) {
                group.on("clusterclick", (event) => {
                    this.ignoreMapClick = true;
                    this.closeDetails();
                    this.fitCluster(event.layer);
                    this.$nextTick(() => {
                        this.ignoreMapClick = false;
                    });
                });
                group.on("animationend", () => {
                    Object.values(this.markersById).forEach((marker) => {
                        if (typeof marker.update === "function") {
                            marker.update();
                        }
                    });
                });
                group.on("spiderfied", (event) => {
                    requestAnimationFrame(() => {
                        this.nudgeIconsIntoView(event.markers || []);
                    });
                });
            }

            group.addTo(this.leafletMap);
            this.clusterGroup = group;
            if (fit && !this.nearestPending) {
                this.fitLocations(this.visibleLocations, { animate });
            }

            if (this.activeId && !this.visibleLocations.some((location) => location.id === this.activeId)) {
                this.activeId = null;
            }
        },

        markersToLocations(markers) {
            return markers.map((marker) => {
                const { lat, lng } = marker.getLatLng();
                return { lat, lon: lng };
            });
        },

        closeDetails({ restoreView = false } = {}) {
            const hadLocation = Boolean(this.activeId);
            if (!hadLocation && !this.overlayExpanded) {
                return;
            }
            this.activeId = null;
            this.overlayExpanded = false;
            if (restoreView && hadLocation && this.mode === "finder") {
                this.scheduleFitVisible({ animate: true });
            }
        },

        beginProgrammaticView() {
            this.programmaticView = true;
            clearTimeout(this.programmaticViewTimer);
            const map = this.leafletMap;
            const clear = () => {
                clearTimeout(this.programmaticViewTimer);
                map?.off("moveend", onMoveEnd);
                this.programmaticViewTimer = setTimeout(() => {
                    this.programmaticView = false;
                }, 0);
            };
            const onMoveEnd = () => clear();
            if (map) {
                map.once("moveend", onMoveEnd);
            }
            this.programmaticViewTimer = setTimeout(clear, FIT_DURATION * 1000 + 200);
        },

        fitCluster(cluster) {
            const map = this.leafletMap;
            if (!map) {
                return;
            }

            const markers = cluster.getAllChildMarkers();
            if (!markers.length) {
                return;
            }

            const token = (this.clusterFitToken = (this.clusterFitToken || 0) + 1);
            let finished = false;
            const afterMove = () => {
                if (finished || token !== this.clusterFitToken) {
                    return;
                }
                finished = true;
                map.off("moveend", afterMove);
                this.revealClusterMarkers(markers);
            };

            if (map.getZoom() >= MAX_ZOOM) {
                afterMove();
                return;
            }

            this.beginProgrammaticView();
            map.stop();

            const bounds = typeof cluster.getBounds === "function"
                ? cluster.getBounds()
                : this.locationBounds(this.markersToLocations(markers));
            const clusterZoom = Number.isFinite(cluster._zoom) ? cluster._zoom : map.getZoom();
            const zoom = Math.min(MAX_ZOOM, Math.max(clusterZoom + 1, map.getZoom() + 1));

            map.once("moveend", afterMove);
            map.flyToBounds(bounds, { ...this.mapPadding(), maxZoom: zoom, duration: FIT_DURATION });
            setTimeout(afterMove, FIT_DURATION * 1000 + 80);
        },

        revealClusterMarkers(markers) {
            const group = this.clusterGroup;
            const marker = markers.find((item) => item);
            if (!group || !marker) {
                return;
            }
            const visible = typeof group.getVisibleParent === "function"
                ? group.getVisibleParent(marker)
                : marker.__parent;
            if (visible && visible !== marker && typeof visible.spiderfy === "function") {
                visible.spiderfy();
                return;
            }
            this.nudgeIconsIntoView(markers);
        },

        locationBounds(locations) {
            if (locations.length === 1) {
                const { lat, lon } = locations[0];
                return L.latLngBounds([
                    [lat, lon],
                    [lat, lon],
                ]);
            }

            const lats = locations.map((location) => location.lat);
            const lons = locations.map((location) => location.lon);
            return L.latLngBounds(
                [Math.min(...lats) - 0.05, Math.min(...lons) - 0.05],
                [Math.max(...lats) + 0.05, Math.max(...lons) + 0.05],
            );
        },

        fitLocations(locations, { animate = false, maxZoom = FIT_MAX_ZOOM } = {}) {
            const map = this.leafletMap;
            if (!map) {
                return;
            }

            this.beginProgrammaticView();
            map.stop();

            if (!locations.length) {
                if (animate) {
                    map.flyTo(SWITZERLAND_CENTER, MIN_ZOOM, { duration: FIT_DURATION });
                }
                return;
            }

            const options = { ...this.mapPadding(), maxZoom };
            const bounds = this.locationBounds(locations);
            if (animate) {
                map.flyToBounds(bounds, { ...options, duration: FIT_DURATION });
            } else {
                map.fitBounds(bounds, options);
            }
        },

        isListed(id) {
            return this.visibleLocations.some((location) => location.id === id);
        },

        overlayPanes() {
            return Array.from(this.$el.querySelectorAll("[data-overlay-pane]"));
        },

        locationIndex(id) {
            return this.locations.findIndex((location) => location.id === id);
        },

        setOverlayPaneState(pane, state) {
            if (!pane) {
                return;
            }
            pane.classList.toggle("is-active", state === "active");
            pane.classList.toggle("is-above", state === "above");
            pane.classList.toggle("is-below", state === "below");
            pane.classList.toggle("is-expanded", state === "active" && this.overlayExpanded);
        },

        replaceOverlaySlot(slot, id, variant) {
            if (!slot) {
                return;
            }
            Array.from(slot.children).forEach((child) => {
                if (typeof Alpine.destroyTree === "function") {
                    Alpine.destroyTree(child);
                }
                child.remove();
            });
            if (!id) {
                return;
            }
            const location = this.locations.find((item) => item.id === id);
            if (!location) {
                return;
            }
            const wrap = document.createElement("template");
            wrap.innerHTML = locationCardHtml(location, this.labels, variant).trim();
            const node = wrap.content.firstElementChild;
            if (!node) {
                return;
            }
            slot.appendChild(node);
            Alpine.initTree(node);
        },

        fillOverlayPane(pane, id) {
            if (!pane) {
                return;
            }
            pane.dataset.locationId = id ? String(id) : "";
            this.replaceOverlaySlot(pane.querySelector(".c-map-section__overlay-desktop"), id, "overlay");
            this.replaceOverlaySlot(pane.querySelector(".c-map-section__overlay-mobile"), id, "overlayMobile");
        },

        syncOverlayPanes(id, prev) {
            const panes = this.overlayPanes();
            if (panes.length < 2) {
                return;
            }

            if (!id) {
                panes.forEach((pane) => this.setOverlayPaneState(pane, "below"));
                return;
            }

            const active = panes.find((pane) => pane.classList.contains("is-active"));
            const incoming = active ? panes.find((pane) => pane !== active) : panes[0];
            const outgoing = active && active !== incoming ? active : null;

            if (incoming.dataset.locationId !== String(id)) {
                this.fillOverlayPane(incoming, id);
            }

            if (!prev || !outgoing) {
                this.setOverlayPaneState(incoming, "active");
                if (outgoing) {
                    this.setOverlayPaneState(outgoing, "below");
                }
                return;
            }

            const fromBelow = this.locationIndex(id) >= this.locationIndex(prev);
            incoming.style.transition = "none";
            this.setOverlayPaneState(incoming, fromBelow ? "below" : "above");
            void incoming.offsetWidth;
            incoming.style.transition = "";
            this.setOverlayPaneState(incoming, "active");
            this.setOverlayPaneState(outgoing, fromBelow ? "above" : "below");
        },

        isMobileFinder() {
            return this.mode === "finder" && !window.matchMedia("(min-width: 1024px)").matches;
        },

        finderPinSize() {
            const scale = this.isMobileFinder() ? 32 / PIN_VIEW.width : 1;
            return {
                pin: [PIN_VIEW.width * scale, PIN_VIEW.height * scale],
                pinAnchor: [PIN_VIEW.tipX * scale, PIN_VIEW.tipY * scale],
                cluster: this.isMobileFinder() ? [56, 56] : [66, 66],
                clusterAnchor: this.isMobileFinder() ? [28, 28] : [33, 33],
            };
        },

        activeOverlayEl() {
            return this.$el.querySelector(".c-map-section__overlay.is-active");
        },

        overlayBox(overlay) {
            const origin = overlay.offsetParent || overlay.parentElement;
            const originRect = origin.getBoundingClientRect();
            const box = overlay.getBoundingClientRect();
            return {
                top: box.top - originRect.top,
                left: box.left - originRect.left,
                width: box.width,
                height: box.height,
            };
        },

        overlayTargetBox(overlay, expanded, compactHeight) {
            const origin = overlay.offsetParent || overlay.parentElement;
            if (expanded) {
                const inset = this.cssLengthPx("--map-overlay-expanded-inset") || 16;
                return {
                    top: inset,
                    left: inset,
                    width: Math.max(0, origin.clientWidth - inset * 2),
                    height: Math.max(0, origin.clientHeight - inset * 2),
                };
            }

            const top = this.cssLengthPx("--map-overlay-compact-top") || 66;
            const side = this.cssLengthPx("--map-overlay-compact-x") || 32;
            return {
                top,
                left: side,
                width: Math.max(0, origin.clientWidth - side * 2),
                height: compactHeight,
            };
        },

        applyOverlayBox(overlay, box) {
            overlay.style.top = `${box.top}px`;
            overlay.style.left = `${box.left}px`;
            overlay.style.width = `${box.width}px`;
            overlay.style.height = `${box.height}px`;
            overlay.style.right = "auto";
            overlay.style.bottom = "auto";
            overlay.style.maxHeight = "none";
        },

        overlayKeyframes(box) {
            return {
                top: `${box.top}px`,
                left: `${box.left}px`,
                width: `${box.width}px`,
                height: `${box.height}px`,
                right: "auto",
                bottom: "auto",
            };
        },

        clearOverlayBox(overlay) {
            if (!overlay) {
                return;
            }
            overlay.getAnimations?.().forEach((animation) => {
                animation.onfinish = null;
                animation.oncancel = null;
                animation.cancel();
            });
            overlay.classList.remove("is-animating");
            overlay.style.top = "";
            overlay.style.left = "";
            overlay.style.width = "";
            overlay.style.height = "";
            overlay.style.right = "";
            overlay.style.bottom = "";
            overlay.style.maxHeight = "";
        },

        setOverlayExpanded(next) {
            next = Boolean(next);
            if (this.overlayExpanded === next) {
                return;
            }

            const overlay = this.activeOverlayEl();
            const reduceMotion =
                !this.isMobileFinder() ||
                !overlay ||
                window.matchMedia("(prefers-reduced-motion: reduce)").matches;

            if (reduceMotion) {
                this.clearOverlayBox(overlay);
                this.overlayExpanded = next;
                overlay?.classList.toggle("is-expanded", next);
                return;
            }

            overlay.getAnimations?.().forEach((animation) => {
                animation.onfinish = null;
                animation.oncancel = null;
                animation.cancel();
            });

            const first = this.overlayBox(overlay);
            if (!this.overlayExpanded) {
                this.compactOverlayBox = first;
            }
            const last = this.overlayTargetBox(
                overlay,
                next,
                (this.compactOverlayBox || first).height,
            );

            overlay.classList.add("is-animating");
            overlay.classList.toggle("is-expanded", next);
            this.applyOverlayBox(overlay, first);
            this.overlayExpanded = next;

            this.$nextTick(() => {
                if (this.activeOverlayEl() !== overlay) {
                    this.clearOverlayBox(overlay);
                    return;
                }

                this.applyOverlayBox(overlay, first);
                void overlay.offsetWidth;

                const animation = overlay.animate([this.overlayKeyframes(first), this.overlayKeyframes(last)], {
                    duration: OVERLAY_ANIM_MS,
                    easing: OVERLAY_ANIM_EASE,
                    fill: "forwards",
                });

                const finish = () => {
                    if (this.activeOverlayEl() === overlay) {
                        this.clearOverlayBox(overlay);
                    }
                };
                animation.onfinish = finish;
                animation.oncancel = finish;
            });
        },

        showDetails(id) {
            const next = this.activeId === id ? null : id;
            this.clearOverlayBox(this.activeOverlayEl());
            this.compactOverlayBox = null;
            this.overlayExpanded = false;
            if (next) {
                this.beginProgrammaticView();
            }
            this.activeId = next;
            if (!next && this.mode === "finder") {
                this.scheduleFitVisible({ animate: true });
            }
            if (next && this.mobileView === "list" && this.isMobileFinder()) {
                this.mobileView = "map";
            }
        },

        scrollListTo(id) {
            if (!id || !this.isListed(id)) {
                return;
            }

            const list = this.$refs.locationList;
            if (!list) {
                return;
            }

            const item = list.querySelector(`[data-location-id="${CSS.escape(String(id))}"]`);
            if (!item) {
                return;
            }

            const listRect = list.getBoundingClientRect();
            const itemRect = item.getBoundingClientRect();
            if (itemRect.top >= listRect.top && itemRect.bottom <= listRect.bottom) {
                return;
            }

            const delta =
                itemRect.top < listRect.top
                    ? itemRect.top - listRect.top
                    : itemRect.bottom - listRect.bottom;
            list.scrollTo({ top: list.scrollTop + delta, behavior: "smooth" });
        },
    }));
});
